/** Shared wire primitives from 05-api.md (Conventions). */
import { z } from 'zod';
import type { Brand } from './brand.js';
import { UUID_PATTERN } from './ids.js';

/** A `YYYY-MM-DD` calendar date in the outlet's timezone and day-close boundary. */
export type BusinessDate = Brand<string, 'BusinessDate'>;

const BUSINESS_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isBusinessDate(value: unknown): value is BusinessDate {
  if (typeof value !== 'string') return false;
  const m = BUSINESS_DATE_PATTERN.exec(value);
  if (!m) return false;
  const [, y, mo, d] = m.map(Number) as [number, number, number, number];
  if (y < 1 || mo < 1 || mo > 12 || d < 1) return false;
  const daysInMonth = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return d <= daysInMonth;
}

/** `YYYY-MM-DD`, and a real calendar date (no Feb 30, leap years honoured). */
export const businessDateSchema = z
  .string()
  .regex(BUSINESS_DATE_PATTERN, { message: 'Expected a date as YYYY-MM-DD', abort: true })
  .refine(isBusinessDate, { message: 'Expected a real calendar date as YYYY-MM-DD' })
  .brand<'BusinessDate'>();

/**
 * An ISO 8601 date-time with an explicit offset (`Z` or `+05:30`). Stored as
 * `timestamptz`; the server emits UTC (`Z`). A local time without an offset
 * is rejected because it is ambiguous.
 */
export const isoTimestampSchema = z.iso.datetime({ offset: true });

/** Default and maximum page size for cursor lists. */
export const DEFAULT_PAGE_LIMIT = 50;
export const MAX_PAGE_LIMIT = 200;

const DIGITS = /^\d+$/;

/**
 * Query strings arrive as text. A blank value (`?limit=`) means "not given"
 * and gets the default. Otherwise only plain decimal digits are converted;
 * anything else (`1e2`, `0x10`, `20.0`, booleans) is passed through unchanged
 * and rejected by `z.number()`. Deliberately stricter than `z.coerce.number()`.
 */
function queryInt(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  const t = v.trim();
  if (t === '') return undefined;
  return DIGITS.test(t) ? Number(t) : v;
}

/** `?limit=50&cursor=…`. */
export const pageQuerySchema = z.object({
  limit: z.preprocess(
    queryInt,
    z.number().int().min(1).max(MAX_PAGE_LIMIT).default(DEFAULT_PAGE_LIMIT),
  ),
  cursor: z.string().min(1).max(512).optional(),
});

export type PageQuery = z.infer<typeof pageQuerySchema>;

/** `{ data: T[], nextCursor }`; `nextCursor` is null on the last page. Cursors are opaque. */
export function paginated<T extends z.ZodType>(item: T) {
  return z.object({
    data: z.array(item),
    nextCursor: z.string().min(1).nullable(),
  });
}

export interface Page<T> {
  data: T[];
  nextCursor: string | null;
}

/** Header names, lowercase as Node/Fastify expose them. */
export const HEADER = {
  idempotencyKey: 'idempotency-key',
  terminalId: 'x-terminal-id',
  overrideToken: 'x-override-token',
  tableToken: 'x-table-token',
} as const;

/**
 * `Idempotency-Key` header value: a UUID. Uppercase input (some platform
 * UUID APIs emit it) is lowercased before validation so the stored key is
 * canonical and a retry with different casing still matches.
 */
export const idempotencyKeySchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(UUID_PATTERN, { message: 'Expected a UUID' })
  .brand<'Uuid'>();
